import React, { useState, useEffect } from 'react';
import { Typography } from 'antd';
import { BarChartOutlined, DollarOutlined, ExperimentOutlined, SettingOutlined, TagsOutlined, FileImageOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AmazonIcon, ShopifyIcon, EtsyIcon, WorkflowIcon } from '../utils/icons';
import { hasPermission } from '../utils/permissions';

const { Text } = Typography;

const Home = () => {
  const { user } = useSelector(state => state.auth);
  const [currentTime, setCurrentTime] = useState(new Date());

  const isSystemAdmin = user?.userRole?.name === 'Admin' || user?.userRole?.name === 'Super Admin' || user?.role === 'Admin';

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour >= 5 && hour < 12) return 'Good morning';
    if (hour >= 12 && hour < 17) return 'Good afternoon';
    if (hour >= 17 && hour < 21) return 'Good evening';
    return 'Good night';
  };

  // Format time
  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Format date
  const formatDate = () => {
    return currentTime.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  const appCategories = [
    {
      key: 'order-operations',
      title: 'Order Operations',
      apps: [
        {
          key: 'amazon-orders',
          title: 'Amazon',
          icon: <AmazonIcon />,
          path: '/orders',
          available: true,
          permission: 'order.read',
        },
        {
          key: 'shopify-orders',
          title: 'Shopify',
          icon: <ShopifyIcon />,
          path: '/shopify/orders',
          available: true,
          permission: 'shopify_order.read',
        },
        {
          key: 'etsy-orders',
          title: 'Etsy',
          icon: <EtsyIcon />,
          path: '#',
          available: false,
          permission: null, // Coming soon, không cần check
        },
      ],
    },
    {
      key: 'optimization',
      title: 'Optimization Tools',
      apps: [
        {
          key: 'analysis',
          title: 'Analysis',
          icon: <BarChartOutlined style={{ color: '#2563eb' }} />,
          path: '/analytics/niche',
          available: true,
          permission: 'sku_item.read',
        },
        {
          key: 'ads-tool',
          title: 'Ads Tool',
          icon: <ExperimentOutlined />,
          path: '/ads-tool',
          available: true,
          permission: 'ads.read',
        },
        {
          key: 'amz-ads',
          title: 'AMZ ADS',
          icon: (
            <span style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#07152b',
              background: '#ffad14',
              boxShadow: '0 4px 10px rgba(255, 173, 20, 0.28)',
            }}>
              <BarChartOutlined style={{ fontSize: 22 }} />
            </span>
          ),
          path: '/amz-ads/profit-report',
          available: true,
          permission: 'profit_report.read',
        },
        {
          key: 'workflow',
          title: 'Workflow',
          icon: <WorkflowIcon />,
          path: '#',
          available: false,
          permission: 'workflow.read', // Coming soon nhưng vẫn check quyền
        },
        {
          key: 'sku-template-group',
          title: 'SKU Template',
          icon: <TagsOutlined style={{ color: '#7C3AED' }} />,
          path: '/optimization/sku-template-group',
          available: true,
          permission: 'design_scale.read',
        },
        {
          key: 'designer-bonus',
          title: 'Designer Bonus',
          icon: <DollarOutlined style={{ color: '#059669' }} />,
          path: '/designer-bonus',
          available: true,
          permission: 'designer_bonus.read',
        },
        {
          key: 'specstudio',
          title: 'Specstudio',
          icon: <FileImageOutlined style={{ color: '#DC2626' }} />,
          path: '/optimization/specstudio',
          available: true,
          permission: 'specstudio.read',
        },
      ],
    },
    {
      key: 'system-management',
      title: 'System Management',
      apps: [
        {
          key: 'system-categories',
          title: 'System Categories',
          icon: <TagsOutlined />,
          path: '/system/categories',
          available: true,
          adminOnly: false,
          permission: 'category.read',
        },
        {
          key: 'system-settings',
          title: 'System Settings',
          icon: <SettingOutlined />,
          path: '/system/settings',
          available: true,
          adminOnly: true,
          permission: null,
        },
      ],
    },
  ];

  // Filter apps based on user permissions
  const filteredCategories = appCategories.map(category => ({
    ...category,
    apps: category.apps.map(app => {
      // Nếu app chưa available (coming soon), giữ nguyên
      if (!app.available) return app;
      // Admin-only app
      if (app.adminOnly) {
        return { ...app, noPermission: !isSystemAdmin };
      }
      // Nếu không có permission required, giữ nguyên
      if (!app.permission) return app;
      // Check permission - nếu không có quyền thì đánh dấu noPermission
      const hasAccess = hasPermission(user, app.permission);
      return { ...app, noPermission: !hasAccess };
    })
  })).filter(category => category.apps.length > 0); // Ẩn category nếu không có app nào

  const AppIcon = ({ app }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    
    // Kiểm tra nếu app không khả dụng hoặc không có quyền
    const isDisabled = !app.available || app.noPermission;

    const iconContent = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          transform: isHovered && !isDisabled ? 'scale(1.05)' : 'scale(1)',
          opacity: isDisabled ? 0.5 : 1,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Icon Container - White background */}
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '14px',
            background: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isHovered && !isDisabled
              ? '0 8px 24px rgba(0, 0, 0, 0.15)'
              : '0 2px 8px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.2s ease',
            border: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          {/* Icon */}
          <div style={{
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {app.icon}
          </div>
        </div>

        {/* App Name */}
        <Text
          style={{
            marginTop: '8px',
            fontSize: '11px',
            fontWeight: 500,
            color: '#374151',
            textAlign: 'center',
            maxWidth: '72px',
            lineHeight: 1.2,
          }}
        >
          {app.title}
        </Text>

        {/* Coming Soon badge */}
        {!app.available && (
          <Text
            style={{
              fontSize: '9px',
              color: '#9ca3af',
              marginTop: '2px',
            }}
          >
            Coming Soon
          </Text>
        )}

        {/* No Permission badge */}
        {app.available && app.noPermission && (
          <Text
            style={{
              fontSize: '9px',
              color: '#9ca3af',
              marginTop: '2px',
            }}
          >
            No Access
          </Text>
        )}
      </div>
    );

    if (!isDisabled && app.path !== '#') {
      return (
        <Link 
          to={app.path} 
          style={{ textDecoration: 'none' }}
          onClick={() => {
            // Set currentApp in localStorage when clicking on app
            localStorage.setItem('currentApp', app.key);
          }}
        >
          {iconContent}
        </Link>
      );
    }

    return iconContent;
  };

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 40px)',
        background: '#f5f5f5',
        padding: '28px',
      }}
    >
      {/* Header - Greeting and Clock */}
      <div
        style={{
          marginBottom: '32px',
        }}
      >
        {/* Greeting */}
        <div
          style={{
            fontSize: '28px',
            fontWeight: 600,
            color: '#1f2937',
            marginBottom: '4px',
          }}
        >
          {getGreeting()}, {user?.username || user?.email?.split('@')[0] || 'User'} 👋
        </div>

        {/* Date and Time */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Text style={{ fontSize: '14px', color: '#6b7280' }}>
            {formatDate()}
          </Text>
          <div
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#f97316',
              fontFamily: 'monospace',
            }}
          >
            {formatTime()}
          </div>
        </div>
      </div>

      {/* Apps Container - Grid layout with frame */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
        }}
      >
        {filteredCategories.map((category) => (
          <div
            key={category.key}
            style={{
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              backgroundColor: '#f2f2f2',
            }}
          >
            {/* Section Title */}
            <Text
              style={{
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '20px',
                display: 'block',
              }}
            >
              {category.title}
            </Text>

            {/* Apps Grid - Left aligned */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, 80px)',
                gap: '24px',
                justifyContent: 'start',
              }}
            >
              {category.apps.map((app) => (
                <AppIcon key={app.key} app={app} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Hint */}
      <div
        style={{
          marginTop: '24px',
          textAlign: 'center',
        }}
      >
        <Text style={{ fontSize: '12px', color: '#9ca3af' }}>
          Click logo in header to return home
        </Text>
      </div>
    </div>
  );
};

export default Home;
